import { Component, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { BehaviorSubject, of } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { DialogStatementsEditCommissionComponent } from '../../components/dialog-statements-edit-commission/dialog-statements-edit-commission.component';
import { AgreementAction, Statement } from '../../../businesses/models/statements.models';
import { StatementsFacade } from '../../../businesses/services/statements.facade';

@Component({
  standalone: false,
  selector: 'sbnb-statements-commissions-edit',
  template: `
    <div class="flex">
      <sbnb-subnav [header]="'OPERATIONS.OPERATIONS' | transloco">
        <sbnb-operations-subnav></sbnb-operations-subnav>
        <sbnb-helptext-wrapper [area]="'sidebar'" [pageId]="'statements-edit-commission'"></sbnb-helptext-wrapper>
      </sbnb-subnav>

      <main class="subnav__exists flex-1" [ngClass.lt-md]="{ mobile: true, subnav__exists: false }">
        <div class="flex flex-col max-w-[750px]">
          <div class="w-full">
            <div class="flex flex-col gap-2 mb-8">
              <app-feature-flag-tabs>
                <sbnb-page-header navTabs>
                  <h2 class="flex-1 mb-0 text-center sbnbDesktop:text-left">Agreement Details</h2>
                </sbnb-page-header>
                <h2 class="flex-1 mb-0">Agreement Details</h2>
              </app-feature-flag-tabs>
              <sbnb-alert-card context="info" *ngIf="commission && !editable">
                <span class="flex gap-2 items-center">
                  Agreements used by published statements cannot be edited or deleted.
                </span>
              </sbnb-alert-card>
            </div>
          </div>
          <sbnb-loading-spinner *ngIf="!commission"></sbnb-loading-spinner>
          <hospitable-statements-commissions-form
            [readonly]="commission && !editable"
            [agreement]="commission"
            (agreementChange)="updatedCommission = $event"
            [showProperties]="false"></hospitable-statements-commissions-form>

          <div *ngIf="editable" class="flex justify-between mt-8">
            <sbnb-button
              class="block"
              type="secondary-danger"
              [disabled]="saving$ | async"
              [loading]="deletingAgreement$ | async"
              (clicked)="delete(commission.id)">
              <span class="flex items-center gap-2">
                <sbnb-icon icon="trash"></sbnb-icon>
                <span>Delete</span>
              </span>
            </sbnb-button>
            <sbnb-button [loading]="(saving$ | async) === true" label="Save" (clicked)="saveDialog()"></sbnb-button>
          </div>
        </div>
      </main>
    </div>

    <sbnb-mobile-sidebar [navTabsHide]="true" [title]="'Operations'">
      <sbnb-operations-subnav></sbnb-operations-subnav>
    </sbnb-mobile-sidebar>
  `,
  styles: [],
})
export class StatementsCommissionsEditComponent implements OnInit {
  public saving$ = new BehaviorSubject<boolean>(false);
  public commission;
  public editable = false;
  public updatedCommission;
  public deletingAgreement$ = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly statementsService: StatementsFacade,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly notificationService: ToastNotificationsService,
    private readonly dialog: MatDialog
  ) {}

  public ngOnInit() {
    this.route.paramMap
      .pipe(
        filter((params) => params.has('id')),
        take(1),
        switchMap((params) => this.statementsService.getCommissionRate(Number(params.get('id')))),
        catchError((error) => {
          return of(null);
        })
      )
      .subscribe((commission) => {
        if (!commission) {
          this.notificationService.error('An error occurred while fetching the commission agreement.');
          this.returnToDashboard();
          return;
        }
        this.commission = commission as any;
        this.editable = commission?.actions?.includes(AgreementAction.Modify);
      });
  }

  public saveDialog() {
    const dialogRef = this.dialog.open(DialogStatementsEditCommissionComponent, {
      width: '720px',
      maxHeight: '90vh',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result !== 'save') {
        return;
      }
      this.save();
    });
  }

  public save() {
    this.saving$.next(true);
    this.statementsService.editCommissionAgreement(this.route.snapshot.params.id, this.updatedCommission).subscribe({
      next: () => {
        this.saving$.next(false);
        this.returnToDashboard();
      },
      error: () => {
        this.saving$.next(false);
      },
    });
  }

  public delete(id: Statement['id']) {
    this.deletingAgreement$.next(true);
    this.statementsService.deleteCommissionAgreement(id).subscribe({
      next: () => {
        this.deletingAgreement$.next(null);
        this.returnToDashboard();
      },
      error: (error) => {
        this.deletingAgreement$.next(null);
        this.notificationService.error(error?.message ?? 'An error occurred while deleting the commission agreement.');
      },
    });
  }

  private returnToDashboard() {
    this.router.navigate(['../../commissions'], { relativeTo: this.route });
  }
}
